import {
	type FormModelFieldSubForm,
	isValidDefaultOwnerField,
	isValidEquipmentHeightField,
	isValidEquipmentTypeField,
	isValidOrientationField,
	isValidOwnerField,
	IkePoleForemanFieldIds,
	PoleFieldIds,
	type FormModelFieldNestedList,
} from '@ikegps/core-shared';
import { m, type MithrilVNode } from '../../../../mithril';
import { SelectInputState } from '../../../../components/input/input.select';
import type { IkeInputStateGroup } from '../../../../components/input/input.state.group';
import type { MenuComponent } from '../../../../components/menu/menu';
import { action, makeObservable, observable } from 'mobx';
import { T, Translate } from '../../../../i18n';
import { MobxState } from '../../../../state/mobx';
import { addStyles } from '../../../../components/theme';
import { SubformEditorDropdown } from './form.field.editor.subform.dropdown';
import { SubFormTypeEditorCSS, Style } from './form.field.editor.subform.style';
import { createMenu, selectedText, stateError } from './form.field.editor.subform.type';

addStyles(Style);

export class EquipmentSubFormTypeEditor {
	private typeFieldSelectState: SelectInputState;
	private orientationFieldSelectState: SelectInputState;
	private ownerFieldSelectState: SelectInputState;
	private attachmentHeightFieldSelectState: SelectInputState;
	private bottomHeightFieldSelectState: SelectInputState;

	private typeMenu: MenuComponent;
	private orientationMenu: MenuComponent;
	private ownerMenu: MenuComponent;
	private attachmentHeightMenu: MenuComponent;
	private bottomHeighthMenu: MenuComponent;

	private reactions: MobxState;
	private _allowCreateNewBottomHeightField: boolean = false;
	private _allowCreateNewOwnerField: boolean = false;
	private _allowCreateNewTypeField: boolean = false;
	@observable private _bottomHeightNoneSelected: boolean = false;
	constructor(
		private field: FormModelFieldSubForm,
		private stateGroup: IkeInputStateGroup
	) {
		makeObservable(this);
		this.reactions = new MobxState();
		const equipmentState = this.field.subFormEquipmentState;

		this.typeFieldSelectState = this.createTypeFieldSelect(equipmentState?.typeFieldId ?? null);
		this.orientationFieldSelectState = this.createOrientationFieldSelect(equipmentState?.orientationFieldId ?? null);
		this.ownerFieldSelectState = this.createOwnerFieldSelect(equipmentState?.ownerFieldId ?? null);
		this.attachmentHeightFieldSelectState = this.createAttachmentHeightFieldSelect(
			equipmentState?.attachmentHeightFieldId ?? null
		);
		this.bottomHeightFieldSelectState = this.createBottomHeightFieldSelect(equipmentState?.bottomHeightFieldId ?? null);

		this.orientationFieldSelectState.isRequired = true;
		this.attachmentHeightFieldSelectState.isRequired = true;

		this.typeMenu = createMenu(this.typeFieldSelectState);
		this.orientationMenu = createMenu(this.orientationFieldSelectState);
		this.ownerMenu = createMenu(this.ownerFieldSelectState);
		this.attachmentHeightMenu = createMenu(this.attachmentHeightFieldSelectState);
		this.bottomHeighthMenu = createMenu(this.bottomHeightFieldSelectState);

		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidEquipmentTypeField)
					.map((f) => f.displayName)
					.join(','),
			() => {
				this.updateTypeFieldOptions();
				this.updateAllowCreateNewTypeField();
			}
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidOrientationField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateOrientationFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidOwnerField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateOwnerFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter((field) => isValidEquipmentHeightField(field, 'attachmentHeightFieldId'))
					.map((f) => `${f.displayName}:${f.collector ?? ''}`)
					.join(','),
			() => this.updateAttachmentHeightFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter((field) => isValidEquipmentHeightField(field, 'bottomHeightFieldId'))
					.map((f) => `${f.displayName}:${f.collector ?? ''}`)
					.join(','),
			() => {
				this.updateBottomHeightFieldOptions();
				this.updateAllowCreateNewBottomHeightField();
			}
		);

		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidOwnerField)
					.map((f) => {
						return JSON.stringify((f as FormModelFieldNestedList).options);
					})
					.join(','),
			() => {
				this.updateAllowCreateNewOwnerField();
			}
		);

		this.updateAllowCreateNewOwnerField();
		this.updateAllowCreateNewBottomHeightField();
		this.updateAllowCreateNewTypeField();

		this.typeFieldSelectState.onValue((value) => {
			if (value) {
				this.typeFieldSelectState.setError(null);
				this.field.Mutator.equipment.selectEquipmentTypeField(value);
			} else {
				this.typeFieldSelectState.setError(Translate(T.INPUT_REQUIRED));
				this.typeFieldSelectState.setTouched();
			}
		});
		this.orientationFieldSelectState.onValue((value) => {
			if (value) {
				this.field.Mutator.equipment.selectOrientationField(value);
			}
		});
		this.ownerFieldSelectState.onValue((value) => {
			if (value) {
				this.ownerFieldSelectState.setError(null);
				this.field.Mutator.equipment.selectOwnerField(value);
			} else {
				this.ownerFieldSelectState.setError(Translate(T.INPUT_REQUIRED));
				this.ownerFieldSelectState.setTouched();
			}
		});
		this.attachmentHeightFieldSelectState.onValue((value) => {
			if (value) {
				this.field.Mutator.equipment.selectAttachmentHeightField(value);
			}
		});
		this.bottomHeightFieldSelectState.onValue((value) => {
			if (value !== null) {
				this._bottomHeightNoneSelected = false;
			}
			this.field.Mutator.equipment.selectBottomHeightField(value);
		});
	}

	private createTypeFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidEquipmentTypeField)
			.map((field) => ({ key: field.displayName, value: field.id }));
		const state = new SelectInputState(Translate(T.EQUIPMENT_TYPE_FIELD), options);
		const valueInOptions = currentValue !== null && options.some((opt) => opt.value === currentValue);
		if (valueInOptions) {
			state.setValue(currentValue);
		} else {
			state.setError(Translate(T.INPUT_REQUIRED));
			state.setTouched();
		}
		this.stateGroup.add(state);
		state.isLabelShown = true;
		return state;
	}
	private createOrientationFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidOrientationField)
			.map((field) => ({ key: field.displayName, value: field.id }));
		const state = new SelectInputState(Translate(T.EQUIPMENT_ORIENTATION_FIELD), options);
		state.setValue(currentValue);
		this.stateGroup.add(state);
		return state;
	}
	private createOwnerFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidOwnerField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		const state = new SelectInputState(Translate(T.ANCHOR_OWNER_FIELD), options);
		const valueInOptions = currentValue !== null && options.some((opt) => opt.value === currentValue);
		if (valueInOptions) {
			state.setValue(currentValue);
		} else {
			state.setError(Translate(T.INPUT_REQUIRED));
			state.setTouched();
		}
		this.stateGroup.add(state);
		state.isLabelShown = true;

		return state;
	}
	private createAttachmentHeightFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter((field) => isValidEquipmentHeightField(field, 'attachmentHeightFieldId'))
			.map((field) => ({ key: field.displayName, value: field.id }));
		const state = new SelectInputState(Translate(T.EQUIPMENT_ATTACHMENT_HEIGHT_FIELD), options);
		state.setValue(currentValue);
		this.stateGroup.add(state);
		return state;
	}
	private createBottomHeightFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter((field) => isValidEquipmentHeightField(field, 'bottomHeightFieldId'))
			.map((field) => ({ key: field.displayName, value: field.id }));
		const state = new SelectInputState(Translate(T.EQUIPMENT_BOTTOM_HEIGHT_FIELD), options);
		state.setValue(currentValue);
		this.stateGroup.add(state);
		return state;
	}

	@action private updateTypeFieldOptions() {
		if (!this.typeFieldSelectState) return;
		const options = this.field.fields
			.filter(isValidEquipmentTypeField)
			.map((field) => ({ key: field.displayName, value: field.id }));
		this.typeFieldSelectState.setOptions(options);
		if (!options.find((opt) => opt.value === this.typeFieldSelectState.value)) {
			this.typeFieldSelectState.setValue(null);
		}
	}
	@action private updateOrientationFieldOptions() {
		if (!this.orientationFieldSelectState) return;
		const options = this.field.fields
			.filter(isValidOrientationField)
			.map((field) => ({ key: field.displayName, value: field.id }));
		this.orientationFieldSelectState.setOptions(options);
		if (!options.find((opt) => opt.value === this.orientationFieldSelectState.value)) {
			this.orientationFieldSelectState.setValue(null);
		}
	}
	@action private updateOwnerFieldOptions() {
		if (!this.ownerFieldSelectState) return;

		const options = this.field.fields
			.filter(isValidOwnerField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.ownerFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.ownerFieldSelectState.value)) {
			this.ownerFieldSelectState.setValue(null);
		}
	}

	@action private updateAttachmentHeightFieldOptions() {
		if (!this.attachmentHeightFieldSelectState) return;

		const options = this.field.fields
			.filter((field) => isValidEquipmentHeightField(field, 'attachmentHeightFieldId'))
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.attachmentHeightFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.attachmentHeightFieldSelectState.value)) {
			this.attachmentHeightFieldSelectState.setValue(null);
		}
	}

	@action private updateBottomHeightFieldOptions() {
		if (!this.bottomHeightFieldSelectState) return;

		const options = this.field.fields
			.filter((field) => isValidEquipmentHeightField(field, 'bottomHeightFieldId'))
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.bottomHeightFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.bottomHeightFieldSelectState.value)) {
			this.bottomHeightFieldSelectState.setValue(null);
		}
	}

	allowCreateNewOwnerField(): boolean {
		return this._allowCreateNewOwnerField;
	}

	@action private updateAllowCreateNewOwnerField() {
		const fieldWithOwnerFieldId = this.field.fields.filter(
			(field) =>
				(field.id === PoleFieldIds.ANCHOR.OWNER || field.id === IkePoleForemanFieldIds.ANCHOR_OWNER) &&
				isValidOwnerField(field)
		);
		const validDefaultOwnerAvailable = this.field.fields.find(isValidDefaultOwnerField);
		this._allowCreateNewOwnerField = fieldWithOwnerFieldId.length === 0 && !validDefaultOwnerAvailable;
	}

	allowCreateNewTypeField(): boolean {
		return this._allowCreateNewTypeField;
	}

	@action private updateAllowCreateNewTypeField() {
		const validTypeField = this.field.fields.find(isValidEquipmentTypeField);
		this._allowCreateNewTypeField = !validTypeField;
	}

	@action createTypeField() {
		const field = this.field.Mutator.equipment.addEquipmentTypeField();

		const options = this.field.fields
			.filter(isValidEquipmentTypeField)
			.map((f) => ({ key: f.displayName, value: f.id }));
		this.typeFieldSelectState.setOptions(options);
		this.typeFieldSelectState.setValue(field.id);
		this.typeFieldSelectState.setError(null);
	}

	@action createOwnerField() {
		const leadLengthIndex = this.field.fields.findIndex(isValidOrientationField);
		const insertIndex = leadLengthIndex >= 0 ? leadLengthIndex : 2;
		const field = this.field.Mutator.equipment.addOwnerField(insertIndex);

		const options = this.field.fields.filter(isValidOwnerField).map((f) => ({ key: f.displayName, value: f.id }));
		this.ownerFieldSelectState.setOptions(options);
		this.ownerFieldSelectState.setValue(field.id);
		this.ownerFieldSelectState.setError(null);
	}

	allowCreateNewBottomHeightField(): boolean {
		return this._allowCreateNewBottomHeightField;
	}

	@action private updateAllowCreateNewBottomHeightField() {
		const fieldWithBottomHeightFieldId = this.field.fields.filter(
			(field) =>
				field.id === PoleFieldIds.EQUIPMENT.ATTACHMENT_BOTTOM_HEIGHT &&
				isValidEquipmentHeightField(field, 'bottomHeightFieldId')
		);
		const validBottomHeightAvailable = this.field.fields.find((field) =>
			isValidEquipmentHeightField(field, 'bottomHeightFieldId')
		);
		this._allowCreateNewBottomHeightField = fieldWithBottomHeightFieldId.length === 0 && !validBottomHeightAvailable;
	}
	@action private selectBottomHeightNone() {
		this._bottomHeightNoneSelected = true;
		this.bottomHeightFieldSelectState.setValue(null);
	}

	@action createBottomHeightField() {
		const attachmentHeightFieldIndex = this.field.fields.findIndex((field) =>
			isValidEquipmentHeightField(field, 'bottomHeightFieldId')
		);
		const insertIndex = attachmentHeightFieldIndex >= 0 ? attachmentHeightFieldIndex : 2;
		const field = this.field.Mutator.equipment.addBottomHeightField(insertIndex);

		const options = this.field.fields
			.filter((field) => isValidEquipmentHeightField(field, 'bottomHeightFieldId'))
			.map((f) => ({ key: f.displayName, value: f.id }));
		this.bottomHeightFieldSelectState.setOptions(options);
		this.bottomHeightFieldSelectState.setValue(field.id);
	}

	view(): MithrilVNode {
		return m('div', { className: SubFormTypeEditorCSS.wrapper }, [
			m('hr'),
			m('div', { className: SubFormTypeEditorCSS.grid }, [
				SubformEditorDropdown.view({
					title: Translate(T.EQUIPMENT_TYPE_FIELD),
					selectedText: selectedText(this.typeFieldSelectState),
					menu: this.typeMenu,
					error: stateError(this.typeFieldSelectState),
					actionBtn: this.allowCreateNewTypeField()
						? { action: () => this.createTypeField(), btnText: Translate(T.FORM_FIELD_CREATE) }
						: undefined,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.EQUIPMENT_ORIENTATION_FIELD),
					selectedText: selectedText(this.orientationFieldSelectState),
					menu: this.orientationMenu,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.EQUIPMENT_OWNER_FIELD),
					selectedText: selectedText(this.ownerFieldSelectState),
					menu: this.ownerMenu,
					error: stateError(this.ownerFieldSelectState),
					actionBtn: this.allowCreateNewOwnerField()
						? { action: () => this.createOwnerField(), btnText: Translate(T.FORM_FIELD_CREATE) }
						: undefined,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.EQUIPMENT_ATTACHMENT_HEIGHT_FIELD),
					selectedText: selectedText(this.attachmentHeightFieldSelectState),
					menu: this.attachmentHeightMenu,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.EQUIPMENT_BOTTOM_HEIGHT_FIELD),
					selectedText: this._bottomHeightNoneSelected
						? Translate(T.NONE)
						: selectedText(this.bottomHeightFieldSelectState),
					menu: this.bottomHeighthMenu,
					allowNone: true,
					noneSelected: this._bottomHeightNoneSelected,
					onNoneSelect: () => this.selectBottomHeightNone(),
					actionBtn:
						this.allowCreateNewBottomHeightField() || this._bottomHeightNoneSelected
							? { action: () => this.createBottomHeightField(), btnText: Translate(T.FORM_FIELD_CREATE) }
							: undefined,
				}),
			]),
		]);
	}
	destroy() {
		this.reactions.destroy();
		// Remove select states from the parent state group
		this.stateGroup.remove(this.typeFieldSelectState);
		this.stateGroup.remove(this.orientationFieldSelectState);
		this.stateGroup.remove(this.ownerFieldSelectState);
		this.stateGroup.remove(this.attachmentHeightFieldSelectState);
		this.stateGroup.remove(this.bottomHeightFieldSelectState);
	}
}
